import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import { adsApi } from '@/api/client';
import { campaignAdProductToReportId } from '@/api/reportAdProducts';
import type { CampaignAdProduct } from '@/types/spCampaigns';
import type {
  KeywordRecommendationAction,
  KeywordRecommendationsData,
} from '@/types/keywordRecommendations';
import { KeywordNegativeApplyModal } from '@/components/KeywordNegativeApplyModal';
import { KeywordApplyConfirmModal } from '@/components/recommendations/KeywordApplyConfirmModal';
import { KeywordRecommendationsTable } from '@/components/recommendations/KeywordRecommendationsTable';
import { SearchTermRecommendationsTable } from '@/components/recommendations/SearchTermRecommendationsTable';
import { RecommendationDateRangeControls } from '@/components/recommendations/RecommendationDateRangeControls';
import { RecommendationThresholdsBanner } from '@/components/recommendations/RecommendationThresholdsBanner';
import { SyncReportsButton } from '@/components/SyncReportsButton';
import { useRecommendationDateRange } from '@/hooks/useRecommendationDateRange';
import { useKeywordRecommendationActions } from '@/hooks/useKeywordRecommendationActions';
import {
  buildApplyAction,
  buildSearchTermApplyAction,
} from '@/api/keywordRecommendationApply';
import type { RecommendationSortField, RecommendationSortOrder } from '@/api/recommendationUtils';

type CampaignKeywordRecommendationsSectionProps = {
  profileId: string;
  campaignId: string;
  adProduct: CampaignAdProduct;
  adGroupNameById?: Map<string, string>;
};

function parsePayload(data: unknown): KeywordRecommendationsData {
  if (!data || typeof data !== 'object') return {};
  return data as KeywordRecommendationsData;
}

export function CampaignKeywordRecommendationsSection({
  profileId,
  campaignId,
  adProduct,
  adGroupNameById,
}: CampaignKeywordRecommendationsSectionProps) {
  const applyDisabled = adProduct !== 'SP';
  const { preset, setPreset, customRange, setCustomRange, range, invalidRange } =
    useRecommendationDateRange();

  const [keywordSearch, setKeywordSearch] = useState('');
  const [keywordSortBy, setKeywordSortBy] = useState<RecommendationSortField>('cost');
  const [keywordSortOrder, setKeywordSortOrder] = useState<RecommendationSortOrder>('desc');

  const [searchTermSearch, setSearchTermSearch] = useState('');
  const [searchTermActionFilter, setSearchTermActionFilter] = useState<
    KeywordRecommendationAction | 'ALL'
  >('ALL');
  const [searchTermSortBy, setSearchTermSortBy] = useState<RecommendationSortField>('cost');
  const [searchTermSortOrder, setSearchTermSortOrder] = useState<RecommendationSortOrder>('desc');
  const recommendationsPath = `/accounts/${encodeURIComponent(profileId)}/campaigns/recommendations?campaignId=${encodeURIComponent(campaignId)}`;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: [
      'keyword-recommendations',
      profileId,
      range.startDate,
      range.endDate,
      campaignId,
    ],
    queryFn: async () => {
      const res = await adsApi.getKeywordRecommendations(profileId, {
        startDate: range.startDate,
        endDate: range.endDate,
        adProducts: campaignAdProductToReportId(adProduct),
        campaignId,
      });
      return parsePayload(res.data);
    },
    enabled: !!profileId && !!campaignId && !invalidRange,
  });

  const apiKeywords = data?.keywords ?? [];
  const apiSearchTerms = data?.searchTerms ?? [];

  const {
    keywords,
    searchTerms,
    selected,
    bidOverrides,
    pendingApply,
    pendingNegativeRow,
    applying,
    applyMutation,
    toggleSelect,
    handleSelectAll,
    handleBidChange,
    handleDismiss,
    handleBulkApply,
    handleBulkDismiss,
    clearSelection,
    setPendingApply,
    setPendingNegativeRow,
  } = useKeywordRecommendationActions({
    profileId,
    campaignId,
    apiKeywords,
    apiSearchTerms,
  });

  const thresholds = data?.thresholds;
  const hasRecommendations = keywords.length > 0 || searchTerms.length > 0;

  return (
    <div className="bg-surface rounded-xl shadow border border-border p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Sparkles className="h-5 w-5 text-brand" />
            Keyword recommendations
          </h2>
          <p className="mt-1 text-sm text-foreground-muted">
            Automated bid, pause, and search term suggestions for this campaign.
          </p>
          <p className="mt-1 text-xs text-foreground-subtle">
            Recommendation period may differ from the keyword metrics table below (last 7 days).
          </p>
        </div>
        <Link to={recommendationsPath} className="text-sm text-brand hover:underline shrink-0">
          View all profile recommendations →
        </Link>
      </div>

      <RecommendationDateRangeControls
        preset={preset}
        onPresetChange={setPreset}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
        range={range}
        invalidRange={invalidRange}
        isFetching={isFetching}
        onRefresh={() => void refetch()}
      />

      {thresholds ? (
        <RecommendationThresholdsBanner thresholds={thresholds} eligibleCount={data?.eligibleCount} />
      ) : null}

      {invalidRange ? (
        <p className="rounded-xl border border-warning/20 bg-warning-subtle px-3 py-2 text-sm text-warning-ink">
          Select a valid date range.
        </p>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-foreground-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading recommendations…
        </div>
      ) : isError ? (
        <div className="space-y-3 py-8 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-warning" />
          <p className="text-sm text-foreground-muted">{(error as Error)?.message || 'Failed to load'}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-sm text-brand hover:underline"
          >
            Retry
          </button>
        </div>
      ) : data?.noData && !hasRecommendations ? (
        <div className="rounded-xl border border-warning/20 bg-warning-subtle px-4 py-6 text-center">
          <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-warning" />
          <p className="text-sm font-medium text-warning-ink">No keyword report data for this range</p>
          <p className="mb-4 mt-1 text-sm text-warning-ink">
            Sync keyword and search term reports first, then refresh this view.
          </p>
          <SyncReportsButton
            profileId={profileId}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white"
          />
        </div>
      ) : !hasRecommendations ? (
        <p className="rounded-xl border border-border bg-page px-4 py-4 text-sm text-foreground-muted">
          No keyword-level actions for this campaign in this range.
        </p>
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-foreground-muted">
            {data?.eligibleCount ?? 0} eligible keywords · {keywords.length} keyword recommendations
            {searchTerms.length > 0 ? ` · ${searchTerms.length} search term suggestions` : ''}
          </p>

          <KeywordRecommendationsTable
            profileId={profileId}
            rows={keywords}
            selected={selected}
            bidOverrides={bidOverrides}
            applying={applying}
            searchQuery={keywordSearch}
            sortBy={keywordSortBy}
            sortOrder={keywordSortOrder}
            onSearchChange={setKeywordSearch}
            onSortByChange={setKeywordSortBy}
            onSortOrderChange={setKeywordSortOrder}
            onToggleSelect={toggleSelect}
            onSelectAll={handleSelectAll}
            onBidChange={handleBidChange}
            onApply={(action) => setPendingApply([action])}
            onDismiss={handleDismiss}
            onAddNegative={(row) => setPendingNegativeRow(row)}
            onBulkApply={handleBulkApply}
            onBulkDismiss={handleBulkDismiss}
            onClearSelection={clearSelection}
            buildApplyAction={buildApplyAction}
            variant="campaign-scoped"
            adGroupNameById={adGroupNameById}
            applyDisabled={applyDisabled}
          />

          {searchTerms.length > 0 ? (
            <SearchTermRecommendationsTable
              profileId={profileId}
              rows={searchTerms}
              selected={selected}
              bidOverrides={bidOverrides}
              applying={applying}
              searchQuery={searchTermSearch}
              actionFilter={searchTermActionFilter}
              sortBy={searchTermSortBy}
              sortOrder={searchTermSortOrder}
              onSearchChange={setSearchTermSearch}
              onActionFilterChange={setSearchTermActionFilter}
              onSortByChange={setSearchTermSortBy}
              onSortOrderChange={setSearchTermSortOrder}
              onToggleSelect={toggleSelect}
              onSelectAll={handleSelectAll}
              onBidChange={handleBidChange}
              onApply={(action) => setPendingApply([action])}
              onDismiss={handleDismiss}
              onBulkApply={handleBulkApply}
              onBulkDismiss={handleBulkDismiss}
              onClearSelection={clearSelection}
              buildApplyAction={buildSearchTermApplyAction}
              variant="campaign-scoped"
              adGroupNameById={adGroupNameById}
              applyDisabled={applyDisabled}
            />
          ) : null}
        </div>
      )}

      {pendingApply ? (
        <KeywordApplyConfirmModal
          actions={pendingApply}
          pending={applyMutation.isPending}
          onConfirm={() => applyMutation.mutate(pendingApply)}
          onCancel={() => setPendingApply(null)}
        />
      ) : null}

      {pendingNegativeRow ? (
        <KeywordNegativeApplyModal
          row={pendingNegativeRow}
          profileId={profileId}
          pending={applyMutation.isPending}
          onConfirm={(actions) => applyMutation.mutate(actions)}
          onCancel={() => setPendingNegativeRow(null)}
        />
      ) : null}
    </div>
  );
}
